import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import moment from 'moment';
import { Observable, Subject } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BackgroundTaskFactory } from '@nimbus-library/features/background-task/background-task.factory';
import { AgentAssistantAcwViewModel, UserInteractionOutcome } from '@nimbus-library/features/agent-assistant/models/agent-assistant.model';
import { AgentAssistantAcwService } from '@nimbus-library/features/agent-assistant/services/agent-assistant-acw.service';
import { AgentAssistantService } from '@nimbus-library/features/agent-assistant/services/agent-assistant.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { InitialModality, ChatChannelType } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-after-call-work-state',
  templateUrl: './after-call-work-state.component.html',
  styleUrls: ['./after-call-work-state.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AfterCallWorkStateComponent implements OnInit {
  private extendEventTrigger$: Subject<boolean> = new Subject();
  private stopEventTrigger$: Subject<boolean> = new Subject();

  public TranslationKeys = TranslationKeys;
  public Icons = ICONS;
  public UserInteractionOutcome = UserInteractionOutcome;

  public extendDisabled$: Observable<boolean>;
  public stopDisabled$: Observable<boolean>;

  public baseDate$: Observable<moment.Moment>;
  public afterCallWorkSate$: Observable<AgentAssistantAcwViewModel | undefined>;

  public constructor(
    private readonly backgroundTaskFactory: BackgroundTaskFactory,
    private readonly agentAssistantAcwService: AgentAssistantAcwService,
    private readonly agentAssistantService: AgentAssistantService,
    private readonly authService: IAuthenticationService,
  ) {}

  public ngOnInit(): void {
    this.baseDate$ = this.backgroundTaskFactory.getServerWatches();
    this.afterCallWorkSate$ = this.agentAssistantService.getAfterCallWorkState$();

    this.extendDisabled$ = this.extendEventTrigger$.asObservable();
    this.stopDisabled$ = this.stopEventTrigger$.asObservable();

    this.afterCallWorkSate$.pipe(untilDestroyed(this)).subscribe(() => {
      this.extendEventTrigger$.next(false);
      this.stopEventTrigger$.next(false);
    });
  }

  public extend(
    unifiedConversationId: string,
    conversationId: string,
    userSessionId: string,
    initialModality: InitialModality,
    channel?: ChatChannelType,
  ): void {
    this.extendEventTrigger$.next(true);

    const resolvedTenantId = this.authService.getUserInfo()?.tenantId;
    const resolvedUserId = this.authService.getUserInfo()?.userId;

    this.agentAssistantAcwService
      .extendAcw(unifiedConversationId, conversationId, userSessionId, initialModality, channel, resolvedTenantId, resolvedUserId)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  public stop(
    unifiedConversationId: string,
    conversationId: string,
    userSessionId: string,
    initialModality: InitialModality,
    channel?: ChatChannelType,
  ): void {
    this.stopEventTrigger$.next(true);

    const resolvedTenantId = this.authService.getUserInfo()?.tenantId;
    const resolvedUserId = this.authService.getUserInfo()?.userId;

    this.agentAssistantAcwService
      .stopAcw(unifiedConversationId, conversationId, userSessionId, initialModality, channel, resolvedTenantId, resolvedUserId)
      .pipe(untilDestroyed(this))
      .subscribe();
  }
}
